import type { HierarchicalTreemapNode, SizedNode } from '../types';
import type { ReadFieldValue, TreemapSizing } from './toSizing';

const isLeaf = ({ nodes }: HierarchicalTreemapNode): boolean =>
  !nodes || nodes.length === 0;

/* A node's own contribution, which `d3.sum` then adds its descendants' to - so a group counting
   itself would inflate every ancestor by the number of groups beneath it rather than the number
   of components. */
const countLeaves = (node: HierarchicalTreemapNode): number =>
  isLeaf(node) ? 1 : 0;

/**
 * A missing value weighs nothing, which is not the same as being worth nothing: an uncosted
 * capability disappears rather than showing as unmeasured. That is the existing behaviour and
 * this keeps it — distinguishing the two needs somewhere to *draw* the difference, which the
 * node renderers do not have yet.
 */
const toFieldValue =
  (fieldName: string, readFieldValue: ReadFieldValue) =>
  ({ modelId }: HierarchicalTreemapNode): number =>
    /* Clamped, because an area cannot be negative and each of the three layouts breaks its own
       way if handed one: `d3.pack` takes the square root for a radius and gets `NaN`, and the
       treemap and the sunburst divide their box in proportion and come back inverted. The
       quieter case is a "net change" or "budget delta" field where a `+100` and a `-100` under
       one parent cancel, and a subtree with data in it draws nothing at all. The old Treemap
       drops non-positive values for the same reason. */
    Math.max(
      0,
      (modelId === null ? null : readFieldValue(modelId, fieldName)) ?? 0
    );

const toValueOfNode = (
  sizing: TreemapSizing,
  readFieldValue: ReadFieldValue
): ((node: HierarchicalTreemapNode) => number) =>
  sizing.kind === 'count'
    ? countLeaves
    : toFieldValue(sizing.fieldName, readFieldValue);

type SizeNodesArgs = {
  /** What the traversal produced, in the order it produced it. */
  nodes: HierarchicalTreemapNode[];
  sizing: TreemapSizing;
  readFieldValue: ReadFieldValue;
};

/**
 * Resolves what each node weighs, so what crosses into the layout is plain data.
 *
 * These are the rules an area means — a group weighs nothing of its own, a missing value weighs
 * nothing, a negative one clamps — and they read the same for all three views. Held here rather
 * than in a `d3.sum` accessor for two reasons: they are assertable without building a hierarchy,
 * and the view model then never holds a `d3.HierarchyNode`, which the layouts mutate in place.
 *
 * A separate pass over the already-built tree rather than folded into `reduceNodes`.
 * `toTreemapNodes` sits in `viewpointScope$` precisely so the regroup does not re-run per
 * settings emission (54a81d11), and the value depends on `sizeByFieldName` - resolving it at
 * build time would drag the graph rebuild onto every size-by change.
 */
export const sizeNodes = ({
  nodes,
  sizing,
  readFieldValue,
}: SizeNodesArgs): SizedNode[] => {
  const valueOfNode = toValueOfNode(sizing, readFieldValue);
  const size = (node: HierarchicalTreemapNode): SizedNode => ({
    ...node,
    value: valueOfNode(node),
    nodes: node.nodes?.map(size) ?? null,
  });

  return nodes.map(size);
};
